// A scheduling request beside the Coordinator Workbench (`#workbench/<sr_…>`; lane B5 S11, screens-walks §2.2, DC-34):
// its journey ("Propose slots -> Availability -> Create Outlook event + Teams link"), each meeting as the Outlook event
// with its Teams link, meeting ID and delivery status ("Failed sync is visible and retryable."), the attendees' response
// status, the conflicts per attendee and reason, the scheduling SLA (Active Time, Calendar Time in the tooltip) and the
// history. Reschedule and Cancel open their dialogs here; proposing slots and the availability grid live on the full
// scheduling page. An unknown or unauthorised id is the canonical not-found — never another request.

import { useState } from "react";
import type { MeetingOut, RequestSyncOut, SchedulingOpenOutput } from "@/api/tools";
import { schedulingOpen, schedulingRetrySync } from "@/api/tools";
import { AppError, callTool, newIdempotencyKey } from "@/api/runtime";
import { errorText, isClientRefusal, useRead, useWrite } from "@/api/useTool";
import { useRoute } from "@/router";
import { useSession } from "@/session/useSession";
import { NotFound } from "@/views/NotFound";
import {
  ACTION_LABELS, Banner, Button, Conflicts, DIFF_LABELS, Dialog, Due, Field, Flex, HISTORY_LABELS, History, ITINERARY_LABELS,
  Journey, MeetingEvent, Paragraph, Pill, RULE_LABELS, Recorded, Responses, SCHEDULING_LABELS, SCHEDULING_STATUS, SLA_LABELS, SYNC_LABELS,
  SlideOver, Stack, TextArea, TextLink, copyText, formatDateTime, toneOf, type ConflictItem, type HistoryEvent, type ItineraryItem, type JourneyStep,
  type ResponseItem, type StatusWord, type SyncProps,
} from "@/mc";
import { CancelDialog, RescheduleDialog } from "./dialogs";
import { RESCHEDULE_REQUEST_LABELS, SCHEDULING_JOURNEY_LABELS } from "./labels";
import {
  HISTORY_STATUSES, ROLE_WORD, acts, conflictWord, dueTier, formatWord, historyWord, hsId, pageOf, responseStatus, statusWord,
  templateWord, type StatusKey,
} from "./workbench";

type Open = "reschedule" | "cancel" | "recover" | null;

export function RequestSlideOver({ id, onClose, openDialog }: { id: string; onClose: () => void; openDialog: string | null }) {
  const route = useRoute();
  const read = useRead(["scheduling.open", id], () => schedulingOpen({ scheduling_request_id: id }));
  if (read.error && isClientRefusal(read.error)) return <NotFound route={route.raw} />;
  const request = read.data;
  return (
    <SlideOver
      title={request ? templateWord(request.meeting_type) : undefined}
      person={request?.candidate_display_name ? { name: request.candidate_display_name, hueKey: request.candidate_id ?? undefined, candidate: true } : undefined}
      onClose={onClose}
      
    >
      {read.error ? (
        <Banner tone="red" live>{errorText(read.error)}</Banner>
      ) : request ? (
        <Body request={request} openDialog={openDialog} onRefresh={() => void read.refetch()} />
      ) : (
        <MeetingEvent items={[]} loading />
      )}
    </SlideOver>
  );
}

function Body({ request, openDialog, onRefresh }: { request: SchedulingOpenOutput; openDialog: string | null; onRefresh: () => void }) {
  const { manifest, me } = useSession();
  const [open, setOpen] = useState<Open>(openDialog === "reschedule" || openDialog === "cancel" ? openDialog : null);
  const [recorded, setRecorded] = useState(false);
  const [recover, setRecover] = useState<RequestSyncOut | null>(null);
  const tz = request.event_tz;
  const status = statusWord(request.status);
  const ended = HISTORY_STATUSES.has(request.status as StatusKey);
  const live = request.meetings.filter((m) => m.state !== "cancelled");
  const booked = live.find((m) => m.state === "booked" || m.state === "held_tentative") ?? null;
  const canReschedule = acts(manifest, me, "scheduling.reschedule") && !ended && booked !== null
    && (request.status === "scheduled" || request.status === "reschedule_requested");
  const canCancel = acts(manifest, me, "scheduling.cancel") && !ended;
  const canRetry = acts(manifest, me, "scheduling.retry_sync");
  // B12: Manual recovery is the Admin's own cell of Schedule meetings (scheduling.resolve_sync declares roles {admin}, no
  // override holder), so the Admin reaches it ordinarily, as every other act here
  const canRecover = acts(manifest, me, "scheduling.resolve_sync");
  const done = () => {
    setOpen(null);
    setRecover(null);
    setRecorded(true);
    onRefresh();
  };
  const retry = useWrite(schedulingRetrySync, [["scheduling.workbench"], ["scheduling.open", request.scheduling_request_id]]);
  const [retryError, setRetryError] = useState<string | null>(null);
  const doRetry = async (sync: RequestSyncOut) => {
    setRetryError(null);
    try {
      await retry.mutateAsync({ scheduling_request_id: request.scheduling_request_id, integration_sync_id: sync.integration_sync_id });
      done();
    } catch (error) {
      setRetryError(errorText(error));
    }
  };
  const conflicts: ConflictItem[] = live.flatMap((m) =>
    m.conflicts.map((c) => ({ name: ROLE_WORD[c.role] ?? c.role, reason: conflictWord(c.kind) })),
  );

  return (
    <Stack gap={16}>
      {recorded ? <Recorded live /> : null}
      {request.status === "cancelled" ? <Banner tone="neutral">{SCHEDULING_STATUS.cancelled}</Banner> : null}
      <Stack gap={8}>
        <Paragraph sm>
          <Pill tone={toneOf(status as StatusWord)}>{status}</Pill>{" "}
          {[request.candidate_display_name, request.requisition_id, request.requisition_title].filter(Boolean).join(" · ")}
        </Paragraph>
        {request.due_at && !ended ? (
          <Due
            tier={dueTier(request.risk)}
            phrase={formatDateTime(request.due_at, tz)}
            at={request.due_at}
            calendar={`${SLA_LABELS.activeTime} · ${RULE_LABELS.slaUsesActiveTime}`}
            ring={{ pct: request.risk === "breached" ? 100 : request.risk === "at_risk" ? 80 : 40 }}
          />
        ) : null}
      </Stack>
      <Journey label={templateWord(request.meeting_type)} sm steps={journey(request)} />
      {!ended && booked === null ? (
        <TextLink href={pageOf({ ...request, scheduling_request_id: request.scheduling_request_id })} >
          {ACTION_LABELS.proposeSlots}
        </TextLink>
      ) : null}
      {retryError ? <Banner tone="red" live>{retryError}</Banner> : null}
      {live.map((meeting) => (
        <MeetingEvent
          key={meeting.meeting_event_id}
          title={meeting.round_label ?? templateWord(request.meeting_type)}
          // the Teams link's and the itinerary's copy controls in words (DC-241): neither is an icon button whose tooltip
          // opens on focus and takes the first Escape, so Escape closes the slide-over wherever the focus is (B5 S11)
          items={withTeams(itinerary(meeting), meeting, `sched.teams-copy.${hsId(meeting.meeting_event_id)}`)}
          copyInWords
          meetingId={meeting.teams_meeting_id ?? meeting.provider_event_id ?? undefined}
          sync={syncOf(meeting, request.syncs ?? [], tz, {
            canRetry, canRecover, retrying: retry.isPending,
            onRetry: (sync) => void doRetry(sync),
            onRecover: (sync) => setRecover(sync),
          })}
          note={meeting.sync_state === "failed" && !canRecover ? SYNC_LABELS.recoveryAvailable : undefined}
          canReschedule={canReschedule && meeting.meeting_event_id === booked?.meeting_event_id}
          onReschedule={() => setOpen("reschedule")}
          hs={{ reschedule: `sched.reschedule.${hsId(meeting.meeting_event_id)}` }}
        >
          <Responses items={responses(meeting)} mode="view" />
        </MeetingEvent>
      ))}
      {conflicts.length > 0 ? <Conflicts items={conflicts}  /> : null}
      {request.history ? (
        <History
          label={HISTORY_LABELS.history}
          timeZone={tz}
          events={request.history.map(historyEvent)}
        />
      ) : null}
      {canCancel ? (
        <div>
          <Button tone="ghost" icon="x" onClick={() => setOpen("cancel")} >{ACTION_LABELS.cancel}</Button>
        </div>
      ) : null}
      {open === "reschedule" && booked && canReschedule ? (
        <RescheduleDialog request={request} meeting={booked} onClose={() => setOpen(null)} onDone={done} />
      ) : null}
      {open === "cancel" && canCancel ? <CancelDialog request={request} onClose={() => setOpen(null)} onDone={done} /> : null}
      {recover ? <RecoverDialog request={request} sync={recover} onClose={() => setRecover(null)} onDone={done} /> : null}
    </Stack>
  );
}

/** Propose slots → Availability → Create Outlook event + Teams link, as far as the request has come. */
function journey(request: SchedulingOpenOutput): JourneyStep[] {
  const reached: Record<string, number> = {
    requested: 0, in_progress: 1, awaiting_candidate: 1, awaiting_interviewer: 1, scheduled: 3, reschedule_requested: 3, completed: 3,
    cancelled: -1,
  };
  const at = reached[request.status] ?? 0;
  const state = (index: number): JourneyStep["state"] => (at < 0 ? "lock" : index < at ? "done" : index === at ? "active" : "next");
  return [
    { label: ACTION_LABELS.proposeSlots, state: state(0) },
    { label: SCHEDULING_JOURNEY_LABELS.availability, state: state(1) },
    { label: SCHEDULING_JOURNEY_LABELS.createEvent, state: state(2) },
  ];
}

/** When (with the round's virtual, onsite or hybrid) · Attendees · Room · Teams link, in the event's time zone (S-4; DS #49). */
/** DC-241: the Teams link in words with its copy control in words (*Copy Teams link*), never an icon alone — on the
 *  scheduling card and in the Workbench slide-over (an icon button's tooltip opens on focus and keeps the first Escape). */
export function withTeams(items: ItineraryItem[], meeting: MeetingOut, copyHs = `sched.teams.copy.${hsId(meeting.meeting_event_id)}`): ItineraryItem[] {
  const url = meeting.teams_url;
  if (!url) return items;
  return items.map(([term, value]) => term !== ITINERARY_LABELS.whereLink ? [term, value] as const : [term, (
    <Flex gap={8} align="center" wrap>
      {value !== undefined && value !== null && value !== "" ? <span>{value}</span> : null}
      <TextLink href={url} icon="video" hs={`sched.teams.${hsId(meeting.meeting_event_id)}`}>{SCHEDULING_LABELS.teamsLink}</TextLink>
      <Button sm tone="ghost" icon="copy" onClick={() => void copyText(url)} hs={copyHs}>
        {RESCHEDULE_REQUEST_LABELS.copyTeamsLink}
      </Button>
    </Flex>
  )] as const);
}

export function itinerary(meeting: MeetingOut): ItineraryItem[] {
  const tz = meeting.event_tz;
  const when = meeting.starts_at ? `${formatDateTime(meeting.starts_at, tz)} · ${tz}` : "—";
  const people = attendeesOf(meeting).filter((a) => a.role !== "coordinator").map((a) => a.name).join(", ");
  const format = formatWord(meeting.format);
  return [
    [ITINERARY_LABELS.when, format ? `${when} · ${format}` : when],
    [ITINERARY_LABELS.with, people || "—"],
    [ITINERARY_LABELS.whereLink, meeting.room_slug ?? ""],
  ];
}

/** The meeting's attendees: never its organizer (DC-240, C7) — the Coordinator who scheduled it is named on its own
 *  line above the responses, is not invited, does not answer and is never counted. */
export function attendeesOf(meeting: MeetingOut): MeetingOut["attendees"] {
  const organizer = meeting.organizer?.person_id;
  return organizer ? meeting.attendees.filter((a) => a.person_id !== organizer) : meeting.attendees;
}

/** The attendees' response status (G §10); a legacy organiser row still carries the *Coordinator* tag. */
export function responses(meeting: MeetingOut): ResponseItem[] {
  return attendeesOf(meeting).map((a) => ({
    key: a.person_id,
    name: a.name,
    sub: ROLE_WORD[a.role] ?? a.role,
    hueKey: a.person_id,
    status: responseStatus(a.response),
    coordinator: a.role === "coordinator",
  }));
}

export interface SyncHandlers {
  canRetry: boolean;
  canRecover: boolean;
  retrying: boolean;
  onRetry: (sync: RequestSyncOut) => void;
  onRecover: (sync: RequestSyncOut) => void;
}

/** The meeting's delivery status from its latest provider call: synchronizing, delivered, or Sync failure with Retry and
 *  the Error details (Correlation ID · status · retries · error details · last attempt). */
export function syncOf(meeting: MeetingOut, syncs: readonly RequestSyncOut[], tz: string, h: SyncHandlers): SyncProps {
  const mine = syncs.filter((s) => s.meeting_event_id === meeting.meeting_event_id);
  const failed = mine.find((s) => s.status === "failed" || s.status === "unknown");
  if (failed || meeting.sync_state === "failed" || meeting.sync_state === "unknown") {
    const row = failed ?? mine[0];
    return {
      status: "failure",
      timeZone: tz,
      details: row ? {
        correlationId: row.correlation_id,
        status: row.status,
        retries: String(row.attempts),
        error: row.last_error ?? undefined,
        lastAttempt: formatDateTime(row.updated_at, tz),
      } : { correlationId: meeting.correlation_id },
      canRetry: h.canRetry && row !== undefined,
      onRetry: row ? () => h.onRetry(row) : undefined,
      retrying: h.retrying,
      canOverride: h.canRecover && row !== undefined,
      onRecover: row ? () => h.onRecover(row) : undefined,
      hs: `sched.retry.${hsId(meeting.meeting_event_id)}`,
      recoverHs: `sched.recover.${hsId(meeting.meeting_event_id)}`,
    };
  }
  if (meeting.sync_state === "pending") return { status: "synchronizing", timeZone: tz };
  const last = mine.find((s) => s.completed_at);
  return { status: "delivered", timeZone: tz, at: last?.completed_at ?? undefined };
}

function historyEvent(event: NonNullable<SchedulingOpenOutput["history"]>[number]): HistoryEvent {
  const from = event.detail["from"] as { starts_at?: string } | undefined;
  const to = event.detail["to"] as { starts_at?: string } | undefined;
  const moved = from?.starts_at && to?.starts_at && from.starts_at !== to.starts_at;
  const word = historyWord(event);
  return {
    id: event.id,
    actor: event.actor_name ? { name: event.actor_name, hueKey: event.actor_id ?? undefined } : undefined,
    sentence: word,
    at: event.at,
    tone: event.kind === "sync_failed" ? "red" : event.kind.startsWith("conflict") ? "flag" : undefined,
    audit: moved ? { old: formatDateTime(from.starts_at!, event.event_tz), new: formatDateTime(to.starts_at!, event.event_tz) } : undefined,
  };
}

/** Manual recovery (the Admin, DC-228): a reason, then the call is marked recovered. */
function RecoverDialog({ request, sync, onClose, onDone }: { request: SchedulingOpenOutput; sync: RequestSyncOut; onClose: () => void; onDone: () => void }) {
  const [reason, setReason] = useState("");
  const [refused, setRefused] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    setRefused(null);
    try {
      await callTool("scheduling.resolve_sync", {
        scheduling_request_id: request.scheduling_request_id, integration_sync_id: sync.integration_sync_id, reason: reason.trim(),
      }, { idempotencyKey: newIdempotencyKey(), reason: reason.trim() });
      onDone();
    } catch (error) {
      setRefused(error instanceof AppError ? errorText(error) : String(error));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog
      preset="override"
      onClose={onClose}
      refused={refused ? { reason: refused } : undefined}
      primary={{
        label: SYNC_LABELS.manualRecovery,
        onClick: () => void submit(),
        busy,
        blocked: reason.trim().length >= 3 ? undefined : { reason: DIFF_LABELS.reason },
        hs: "sched.recover.submit",
      }}
      
    >
      <Field label={DIFF_LABELS.reason} required>
        <TextArea value={reason} onChange={(event) => setReason(event.target.value)} maxLength={500}  />
      </Field>
    </Dialog>
  );
}

